import React, { useState, useEffect } from "react";
import QRCode from "react-qr-code";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ShoppingCartOutlined, CloseOutlined, InfoOutlined, Timer, Refresh } from "@mui/icons-material";
import { Box, Typography, Modal, Button, CircularProgress, Drawer, Divider, Tooltip, Zoom, FormControlLabel, Checkbox } from "@mui/material";
import logoMeLi from "../../../../../../assets/logoMeLi.svg";
import logoPagotic from "../../../../../../assets/logoPagotic.svg";
import logoPVS from "../../../../../../assets/pvs-logo.jpg"
import logoClickMacro from "../../../../../../assets/clickmacro-logo.jpg";
import SecondDrawer from "../second-drawer/SecondDrawer";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { api, fetchCSRFToken } from "../../../../../../services/api";
import { useAuth } from "../../../../../../components/context/AuthContext";
import { usePolling } from "../../../../../../hooks/usePolling";

const FirstDrawerDesktop = ({
  data,
  cantidad,
  setCantidad,
  toggleDrawer,
  openDrawer,
  tarifasSeleccionadas,
  asientosSeleccionados,
  setAsientosSeleccionados,
}) => {
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [loadingCarrito, setLoading] = useState(false);
  const { isAuthenticated, loginWithRedirect } = useAuth();
  const param = useParams();
  const [openSecondDrawer, setOpenSecondDrawer] = useState(false);
  const [dataTarifas, setDataTarifas] = useState([]);
  const [medioPago, setMedioPago] = useState("mercadopago");
  const [multiplesMetodosPago, setMultiplesMetodosPago] = useState(false);

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrData, setQrData] = useState(null);

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const [qrExpirationTime, setQrExpirationTime] = useState(5 * 60);
  const [qrExpirationInterval, setQrExpirationInterval] = useState(null);

  const {
    isPolling,
    paymentStatus,
    paymentReference,
    startPolling,
    stopPolling,
    resetPolling,
    checkPaymentStatus
  } = usePolling();

  useEffect(() => {
    // verificar si se ha creado un link
    const lastLinkTimestamp = localStorage.getItem('lastPaymentLinkCreated');

    if (lastLinkTimestamp) {
      const now = Date.now();
      const lastTime = parseInt(lastLinkTimestamp);
      const cooldownPeriod = 1 * 60 * 1000; // 5 minutos
      const timeElapsed = now - lastTime;

      // chequear si estamos en conldwon
      if (timeElapsed < cooldownPeriod) {
        setCooldownActive(true);
        setCooldownRemaining(Math.ceil((cooldownPeriod - timeElapsed) / 1000));
      } else {
        setCooldownActive(false);
        setCooldownRemaining(0);
      }
    }
  }, []);

  useEffect(() => {
    let timer;
    if (cooldownActive && cooldownRemaining > 0) {
      timer = setInterval(() => {
        setCooldownRemaining(prev => {
          if (prev <= 1) {
            setCooldownActive(false);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldownActive, cooldownRemaining]);

  const startPaymentStatusPolling = (reference, provider = 'clickmacro') => {
    console.log(`Iniciando polling para referencia: ${reference}, provider: ${provider}`);

    startPolling(reference, provider, {
      onSuccess: (result) => {
        console.log("Pago aprobado:", result);
        setQrModalOpen(false);
        limpiarDespuesDeCompra();
        toast.success("¡Pago aprobado! Redirigiendo...", {
          position: "top-right",
          autoClose: 2000,
        });
        setTimeout(() => {
          window.location.href = `/mis-compras`;
        }, 2000);
      },
      onRejected: (result) => {
        console.log("Pago rechazado:", result);
        toast.error("Pago rechazado", {
          position: "top-right",
          autoClose: 4000,
        });
      },
      onTimeout: () => {
        console.log("Timeout del polling - usuario canceló o no quiere seguir esperando");
        setQrModalOpen(false);
        resetQR();
        toast.warning("Operación cancelada", {
          position: "top-right",
          autoClose: 3000,
        });
      },
      onMaxAttemptsReached: () => {
        console.log("Máximo de intentos alcanzado");
        toast.warning("Tiempo de espera agotado", {
          position: "top-right",
          autoClose: 4000,
        });
      }
    });
  };

  const handlePagoDirecto = async () => {
    if (loadingCarrito || cooldownActive) return;

    setButtonDisabled(true);
    setLoading(true);

    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
      setButtonDisabled(false);
      setLoading(false);
      return;
    }

    const idevento = parseInt(param.id);
    const items = Object.entries(cantidad)
      .map(([idtarifa, cantidad]) => {
        const tarifaObj = tarifasSeleccionadas.find(t => t.id === parseInt(idtarifa));
        const datosAsientos = asientosSeleccionados[idtarifa] || { asientos: [], mesas: {} };

        let asientosParaEnviar = [];

        if (datosAsientos.asientos && datosAsientos.asientos.length > 0) {
          asientosParaEnviar = datosAsientos.asientos;
        } else if (datosAsientos.mesas && Object.keys(datosAsientos.mesas).length > 0) {
          asientosParaEnviar = [];
        }

        const mesaId = Object.keys(datosAsientos.mesas)[0] ? parseInt(Object.keys(datosAsientos.mesas)[0]) : null;

        const tarifaResponses = Object.entries(dataTarifas)
          .filter(([key]) => key.startsWith(`${idtarifa}-`))
          .map(([, data]) => {
            return Object.entries(data.respuestas || {}).map(([preguntaId, respuesta]) => ({
              id_informacion_adicional: parseInt(preguntaId),
              respuesta: respuesta || "",
            }));
          });

        const item = {
          idtarifa: parseInt(idtarifa),
          cantidad,
          respuestas: tarifaResponses.flat(),
          idmesa: mesaId
        };

        const esMesaNoNumerada = tarifaObj?.ubicacion.tipo_ubicacion === 2;
        const esPreimpreso = tarifaObj?.preimpreso === true;

        if (
          !(
            esMesaNoNumerada &&
            !esPreimpreso
          )
        ) {

          if (asientosParaEnviar.length > 0 || (esMesaNoNumerada && esPreimpreso)) {
            item.asientos_seleccionados = JSON.stringify(asientosParaEnviar);
          }
        }

        return item;
      })
      .filter((item) => item.cantidad > 0);

    if (items.length === 0) {
      toast.info("Debes seleccionar al menos una tarifa para continuar.", {
        position: "top-right",
        autoClose: 4000,
      });
      setLoading(false);
      return;
    }

    /* console.log("INICIO", medioPago); */
    try {
      let csrfToken = sessionStorage.getItem('csrf_token');
      if (!csrfToken) {
        csrfToken = await fetchCSRFToken();
      }

      if (!csrfToken) {
        throw new Error("No se pudo obtener el token de seguridad. Por favor, intente nuevamente.");
      }
      /* console.log("Medio de pago seleccionado:", medioPago); */
      const payload = {
        idevento,
        items,
        pago_qr: false,
        medio_pago: medioPago,
        csrf_token: csrfToken,
      };
      /* console.log("Payload enviado:", payload);  */
      const response = await api.carrito.crearCarrito(payload);
      if (response.status === "success") {
        localStorage.setItem('lastPaymentLinkCreated', Date.now().toString());
        setCooldownActive(true);
        setCooldownRemaining(1 * 60);

        // segun el metodo de pago delegar a otro handle
        await procesarRespuestaCarrito(response);
      } else {
        throw new Error(response.message || "Error al procesar el pago.");
      }
    } catch (error) {
      toast.error(error.message, {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  //segun la rta del carrito delegamos a un handle segun el mp elegido
  const procesarRespuestaCarrito = async (response) => {
    switch (medioPago) {
      case 'clickmacro-qr':
        await handleClickMacroQR(response.data.id);
        break;
      case 'pvs-qr':
        await handlePVSQR(response.data.id);
        break;
      case 'mercadopago-qr':
        await handleMercadoPagoQR(response.data.id);
        break;
      default:
        // para pago x link o gratuitos
        handleRegularPayment(response);
        break;
    }
  };

  const handleClickMacroQR = async (carritoId) => {
    try {
      const clickMacroResponse = await api.clickmacro.generateClickMacroQr({
        idcarrito: carritoId,
        descripcion: `Compra en evento ${param.id}`
      }, 'online');

      if (clickMacroResponse.status === "success") {
        if (!clickMacroResponse.data.reference) {
          toast.error("Error con la referencia de pago", {
            position: "top-right",
            autoClose: 4000,
          });
          return;
        }

        setQrData({
          ...clickMacroResponse.data,
          provider: 'clickmacro'
        });
        setQrModalOpen(true);

        startPaymentStatusPolling(clickMacroResponse.data.reference, 'clickmacro');
      } else {
        throw new Error(clickMacroResponse.message || "Error al generar QR de Click Macro");
      }
    } catch (error) {
      toast.error(`Error al generar el código QR: ${error.message}`, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handlePVSQR = async (carritoId) => {
    try {
      const pvsResponse = await api.pvs.generatePVSQr({
        idcarrito: carritoId,
        descripcion: `Compra en evento ${param.id}`
      });

      if (pvsResponse.status === "success") {
        setQrData({
          ...pvsResponse.data,
          provider: 'pvs'
        });
        setQrModalOpen(true);

        if (pvsResponse.data.qrId) {
          startPaymentStatusPolling(pvsResponse.data.qrId, 'pvs');
        }
      } else {
        throw new Error(pvsResponse.message || "Error al generar QR de PVS");
      }
    } catch (error) {
      toast.error(`Error al generar el código QR: ${error.message}`, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleMercadoPagoQR = async (carritoId) => {
    try {
      const qrResponse = await api.mercadopago.generateQR(carritoId);

      if (qrResponse.status === 'success') {
        setQrData({
          qrData: qrResponse.data.qr_data,
          provider: 'mercadopago',
          reference: qrResponse.data.reference
        });
        setQrModalOpen(true);

        /* if (qrResponse.data.reference) {
          startPolling(qrResponse.data.reference, 'mercadopago', {
            onSuccess: (result) => {
              setQrModalOpen(false);
              toast.success("¡Pago aprobado! Redirigiendo...", {
                position: "top-right",
                autoClose: 2000,
              });
              setTimeout(() => {
                window.location.href = `/mis-compras`;
              }, 2000);
            },
            onRejected: (result) => {
              toast.error("Pago rechazado", {
                position: "top-right",
                autoClose: 4000,
              });
            }
          });
        } */
      } else {
        throw new Error(qrResponse.message || "Error generando QR de MercadoPago");
      }
    } catch (error) {
      toast.error(`Error al generar el código QR: ${error.message}`, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleRegularPayment = (response) => {
    const esCompraGratuita = response.message && response.message.includes("gratuitos");
    const hayUrlPago = response.data.url_pago && response.data.url_pago !== "";

    if (esCompraGratuita || !hayUrlPago) {
      // COMPRA DE SOLO TICKETS GRATUITOS
      toast.success("¡Tickets gratuitos generados correctamente! Se enviaron a tu email.", {
        position: "top-right",
        autoClose: 3000,
      });

      setAsientosSeleccionados({});

      setTimeout(() => {
        window.location.href = `/mis-compras`;
      }, 2000);
    } else {
      setAsientosSeleccionados({});
      window.location.href = response.data.url_pago;
    }
  };

  const limpiarDespuesDeCompra = () => {
    setAsientosSeleccionados({});
    setCantidad({});
    setMostrarFormulario(false);
    setOpenSecondDrawer(false);
  };

  useEffect(() => {
    if (qrModalOpen && qrData) {
      setQrExpirationTime(5 * 60);

      const interval = setInterval(() => {
        setQrExpirationTime(prevTime => {
          if (prevTime <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      setQrExpirationInterval(interval);

      return () => {
        clearInterval(interval);
      };
    }
  }, [qrModalOpen, qrData]);

  useEffect(() => {
    return () => {
      if (qrExpirationInterval) {
        clearInterval(qrExpirationInterval);
      }
    };
  }, [qrExpirationInterval]);

  const resetQR = () => {
    setQrData(null);
    setQrModalOpen(false);
    setMostrarFormulario(false);
    setOpenSecondDrawer(false);
    setCantidad({});
    stopPolling();
    resetPolling();
  };
  const handleCloseQrModal = () => {
    const userConfirmed = window.confirm(
      "🚨 ¡ATENCIÓN! 🚨\n\n" +
      "Esta pestaña debe permanecer ABIERTA para completar tu pago.\n\n" +
      "❌ Si cierras ahora y ya pagaste:\n" +
      "   • NO recibiremos tu pago automáticamente\n" +
      "   • Tendrás que contactar soporte para verificar tu pago\n\n" +
      "✅ Para completar tu pago:\n" +
      "   • Escanea el QR con tu app de pago\n" +
      "   • Recibes los tickets al instante\n\n" +
      "¿REALMENTE quieres cerrar y perder tu progreso?"
    );

    if (userConfirmed) {
      resetQR();
    }
  };

  const handleRefreshPayment = async () => {
    if (paymentReference && qrData?.provider) {
      const result = await checkPaymentStatus(paymentReference, qrData.provider);
      if (result) {
        if (result.status === "APROBADO") {
          setQrModalOpen(false);
          limpiarDespuesDeCompra();
          toast.success("¡Pago aprobado! Redirigiendo...", {
            position: "top-right",
            autoClose: 2000,
          });
          setTimeout(() => {
            window.location.href = `/mis-compras`;
          }, 2000);
        } else if (result.status === "RECHAZADO") {
          toast.error("Pago rechazado", {
            position: "top-right",
            autoClose: 4000,
          });
        } else {
          toast.info(`Estado actual: ${result.status}`, {
            position: "top-right",
            autoClose: 2000,
          });
        }
      }
    }
  };

  const formatQRImage = (qrImage) => {
    if (!qrImage) return '';
    if (qrImage.startsWith('data:')) {
      return qrImage;
    }
    return `data:image/png;base64,${qrImage}`;
  };

  const formatRemainingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const checkTarifasPreguntas = () => {
    const hasQuestions = tarifasSeleccionadas.some(tarifa => {
      if (tarifa.informacion_adicional === false) {
        return false;
      }

      return tarifa.adicionales?.some(adicional => {
        return adicional.informacion_adicional.length > 0;
      });
    });
    return hasQuestions;
  };

  const handleButtonClick = () => {
    if (loadingCarrito) return;

    if (checkTarifasPreguntas()) {
      handleOpenSecondDrawer();
    } else {
      handlePagoDirecto();
    }
  };

  const handleCloseDrawer = () => {
    const hasSelectedTarifas = Object.values(cantidad).some(
      (value) => value > 0
    );

    if (hasSelectedTarifas) {
      const userConfirmed = window.confirm(
        "¿Estás seguro de que quieres cerrar? Se perderán las tarifas seleccionadas."
      );

      if (userConfirmed) {
        setMostrarFormulario(false);
        setOpenSecondDrawer(false);
        setCantidad({});
        toggleDrawer(false)();
      }
    } else {
      setMostrarFormulario(false);
      setCantidad({});
      toggleDrawer(false)();
    }
  };

  /* CALCULOS TARIFAS */
  const calculoSubtotalTarifa = (tarifa) => {
    const cantidadTarifa = cantidad[tarifa.id] || 0;
    return cantidadTarifa * tarifa.precio;
  };

  const subtotal = data?.tarifas?.reduce((acc, tarifa) => {
    return acc + calculoSubtotalTarifa(tarifa);
  }, 0) || 0;

  const comision = data?.tarifas?.reduce((acc, tarifa) => {
    const cantidadTarifa = cantidad[tarifa.id] || 0;
    const precioConComision =
      tarifa?.precio *
      (!tarifa?.asumir_comision
        ? tarifa?.tipo_comision?.porcentaje / 100
        : 0);

    return acc + cantidadTarifa * precioConComision;
  }, 0) || 0;

  const formatCurrency = (value) =>
    value.toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });

  const calcularTotal = () => {
    const total = subtotal + comision;
    return total;
  };

  const hayComision = data?.tarifas?.some((tarifa) => {
    const cantidadTarifa = cantidad[tarifa.id] || 0;
    return cantidadTarifa > 0 && !tarifa?.asumir_comision;
  });

  /* FIN CALCULOS TARIFAS */

  const handleOpenSecondDrawer = () => {
    setOpenSecondDrawer(true);
  };

  const handleBack = () => {
    setOpenSecondDrawer(false);
  };

  const totalFormateado = formatCurrency(calcularTotal());

  //detectar link en la descripcion para terminos legales
  const [tieneLink, setTieneLink] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  function contieneLink(descripcion) {
    if (!descripcion) return false;

    const parser = new DOMParser();
    const doc = parser.parseFromString(descripcion, "text/html");
    const enlaces = doc.getElementsByTagName("a");

    return enlaces.length > 0;
  }

  useEffect(() => {
    const resultado = contieneLink(data?.descripcion);
    setTieneLink(resultado);
    setCheckboxChecked(false);

    /* console.log("¿El texto tiene un link?", resultado); */
  }, [data]);

  const handleCheckboxChangeTerminos = () => {
    setCheckboxChecked(!checkboxChecked);
  };

  //manejamos los medios de pago
  useEffect(() => {
    if (data?.medios_pago) {
      const tieneMP = data.medios_pago.mercado_pago;
      const tienePagoTic = data.medios_pago.pagotic;
      const tienePVS = data.medios_pago.pvs && param.id !== "465" && param.id !== "478" && param.id !== "479" && param.id !== "480";
      const tienePVSQR = data.medios_pago.pvs;
      const tieneClickMacro = data.medios_pago.click_macro;

      // chequeamos si hay multiples metodos de pago
      const metodosDisponibles = [
        tieneMP && 'mercadopago',
        tienePagoTic && 'pagotic',
        tienePVS && 'pvs-link',
        tienePVSQR && 'pvs-qr',
        tieneClickMacro && 'clickmacro-link'
      ].filter(Boolean);

      setMultiplesMetodosPago(metodosDisponibles.length > 1);

      if (metodosDisponibles.length === 1) {
        setMedioPago(metodosDisponibles[0]);
      } else if (metodosDisponibles.length > 1) {
        // si hay múltiples métodos, por defecto no seleccionamos ninguno
        setMedioPago('');
      }
    }
  }, [data?.medios_pago, param.id]);

  const todasTarifasGratuitas = () => {
    if (!tarifasSeleccionadas || tarifasSeleccionadas.length === 0) return false;

    return tarifasSeleccionadas.every(tarifa => {
      const cantidadSeleccionada = cantidad[tarifa.id] || 0;
      return cantidadSeleccionada > 0 && tarifa.precio === 0;
    });
  };

  const hayTarifasPagas = () => {
    if (!tarifasSeleccionadas || tarifasSeleccionadas.length === 0) return false;

    return tarifasSeleccionadas.some(tarifa => {
      const cantidadSeleccionada = cantidad[tarifa.id] || 0;
      return cantidadSeleccionada > 0 && tarifa.precio > 0;
    });
  };

  return (
    <>
      <Drawer
        sx={{
          minWidth: "80%",
          maxHeight: "85vh",
          padding: "40px",
          "& .MuiDrawer-paper": { borderRadius: "10px 10px 0 0", minHeight: "80%", maxHeight: "85vh", }
        }}
        transitionDuration={500}
        anchor="bottom"
        open={openDrawer}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            handleCloseDrawer();
          }
        }}
      >
        <div style={{ padding: "40px" }}>
          <div
            className="d-flex flex-row justify-content-between"
            style={{}}
          >
            <p
              className="fw-bold mb-3"
              style={{ color: "#221A60", fontSize: "20px" }}
            >
              Resumen de tu compra
            </p>
            <button
              type="button"
              onClick={handleCloseDrawer}
              style={{ paddingRight: "16px" }}
            >
              <CloseOutlined
                sx={{ width: "16px", height: "17px", marginRight: "0px" }}
              />
            </button>
          </div>
          <div className="d-flex flex-row w-100" style={{}}>
            <div className="" style={{ flex: 1, paddingRight: "28px" }}>
              <p
                style={{
                  color: "#221A60",
                  fontSize: "20px",
                  marginBottom: "5px",
                }}
              >
                Tu compra
              </p>
              <div className="d-flex flex-column px-3 pt-4">
                <div className="mb-3">
                  {tarifasSeleccionadas?.length > 0 ? (
                    tarifasSeleccionadas.map((tarifa) => (
                      <div
                        key={tarifa.id}
                        className="d-flex justify-content-between align-items-center py-1 mb-2"
                      >
                        <div className="d-flex flex-column">
                          <span className="">{`${cantidad[tarifa.id]} x ${tarifa.concepto
                            }`}</span>
                        </div>
                        <span className="">
                          ${" "}
                          {calculoSubtotalTarifa(tarifa).toLocaleString(
                            "es-ES",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                              useGrouping: true,
                            }
                          )}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="mb-0">No hay tarifas seleccionadas</p>
                  )}
                </div>

                {tarifasSeleccionadas?.length > 0 && (
                  <>
                    <Divider
                      variant="middle"
                      sx={{ backgroundColor: "black" }}
                    />
                    <div
                      className={`d-flex flex-column ${hayComision ? "py-4" : " pt-3"
                        }`}
                    >
                      <div className="d-flex flex-row justify-content-between">
                        <p className="mb-3">Subtotal</p>
                        <p className="mb-3">$ {formatCurrency(subtotal)}</p>
                      </div>
                      {hayComision && (
                        <div className="d-flex flex-row justify-content-between">
                          <div className="d-flex flex-row align-items-center">
                            <p className="mb-0">Costo</p>
                            <Tooltip
                              arrow
                              TransitionComponent={Zoom}
                              placement="right"
                              title="El costo de servicio de PassGo no es reembolsable"
                            >
                              <InfoOutlined
                                sx={{
                                  width: "18px",
                                  height: "18px",
                                  marginLeft: "5px",
                                }}
                              />
                            </Tooltip>
                          </div>
                          <p className="mb-0">
                            $ {formatCurrency(comision)}
                          </p>
                        </div>
                      )}
                    </div>
                    <Divider
                      variant="middle"
                      sx={{ backgroundColor: "black" }}
                    />
                    <div className="d-flex flex-row justify-content-between py-4">
                      <p className="mb-3 fw-bold">Total</p>
                      <div className="d-flex flex-column text-right">
                        <p className="mb-0 ">
                          $ {formatCurrency(calcularTotal())}
                        </p>
                        <span
                          className="fw-light"
                          style={{ fontSize: "14px" }}
                        >
                          El precio incluye impuestos
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="" style={{ flex: 1, paddingRight: "8px" }}>
              {!todasTarifasGratuitas() && (
                <>
                  <p
                    style={{
                      color: "#221A60",
                      fontSize: "24px",
                      marginBottom: "5px",
                    }}
                  >
                    Pagar con
                  </p>
                  <div style={{ width: "100%", textAlign: "left" }}>
                    {data?.medios_pago?.mercado_pago && (
                      <>
                        {param.id !== "479" && param.id !== "480" && (
                          <>
                            <div
                              className="d-flex justify-content-between align-items-center px-3 mb-2"
                              style={{
                                border: "1px solid #BDBDBD",
                                borderRadius: "4px",
                                backgroundColor: medioPago === 'mercadopago' ? '#f5f5f5' : 'white',
                              }}
                            >
                              <input
                                type="radio"
                                id="mercadopago"
                                name="medioPago"
                                value="mercadopago"
                                checked={medioPago === 'mercadopago'}
                                onChange={(e) => setMedioPago(e.target.value)}
                              />
                              <label htmlFor="mercadopago" className="mb-0 ml-3">
                                Mercado Pago
                              </label>
                              <img
                                src={logoMeLi}
                                alt="Logo Mercado Libre"
                                style={{
                                  width: "45px",
                                  height: "45px",
                                  marginLeft: "auto",
                                }}
                              />
                            </div>
                            <div className="d-flex justify-content-between align-items-center px-3 mb-2"
                              style={{
                                border: "1px solid #BDBDBD",
                                borderRadius: "4px",
                                backgroundColor: medioPago === 'mercadopago-qr' ? '#f5f5f5' : 'white',
                              }}>
                              <input
                                type="radio"
                                id="mercadopago-qr"
                                name="medioPago"
                                value="mercadopago-qr"
                                checked={medioPago === 'mercadopago-qr'}
                                onChange={(e) => setMedioPago(e.target.value)}
                              />
                              <label htmlFor="mercadopago-qr" className="mb-0 ml-3">
                                Mercado Pago QR
                              </label>
                              <img src={logoMeLi} alt="Logo Mercado Pago"
                                style={{ width: "45px", height: "45px", marginLeft: "auto" }} />
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {data?.medios_pago?.pagotic && (
                      <div
                        className="d-flex justify-content-between align-items-center px-3"
                        style={{
                          border: "1px solid #BDBDBD",
                          borderRadius: "4px",
                          backgroundColor: medioPago === 'pagotic' ? '#f5f5f5' : 'white',
                        }}
                      >
                        <input
                          type="radio"
                          id="pagotic"
                          name="medioPago"
                          value="pagotic"
                          checked={medioPago === 'pagotic'}
                          onChange={(e) => setMedioPago(e.target.value)}
                        />
                        <label htmlFor="pagotic" className="mb-0 ml-3">
                          PagoTIC
                        </label>
                        <Tooltip
                          title="PagoTIC es una plataforma segura que te permite pagar con tarjeta de crédito, débito o transferencia bancaria"
                          placement="top"
                          arrow
                        >
                          <InfoOutlined fontSize="small" style={{ color: '#6c757d', cursor: 'help', marginLeft: "10px" }} />
                        </Tooltip>
                        <img
                          src={logoPagotic}
                          alt="Logo PagoTic"
                          style={{
                            width: "45px",
                            height: "45px",
                            marginLeft: "auto",
                          }}
                        />
                      </div>
                    )}

                    {data?.medios_pago?.pvs && (
                      <>
                        {param.id !== "465" && param.id !== "478" && param.id !== "479" && param.id !== "480" && (
                          <div
                            className="d-flex justify-content-between align-items-center px-3 mt-2"
                            style={{
                              border: "1px solid #BDBDBD",
                              borderRadius: "4px",
                              backgroundColor: medioPago === 'pvs-link' ? '#f5f5f5' : 'white',
                            }}
                          >
                            <input
                              type="radio"
                              id="pvs-link"
                              name="medioPago"
                              value="pvs-link"
                              checked={medioPago === 'pvs-link'}
                              onChange={(e) => setMedioPago(e.target.value)}
                            />
                            <label htmlFor="pvs-link" className="mb-0 ml-3">
                              PVS
                            </label>
                            <Tooltip
                              title="Paga a través de un link seguro con tu tarjeta o escanea un QR con tu billetera virtual"
                              placement="top"
                              arrow
                            >
                              <InfoOutlined fontSize="small" style={{ color: '#6c757d', cursor: 'help', marginLeft: "10px" }} />
                            </Tooltip>
                            <img
                              src={logoPVS}
                              alt="Logo PVS"
                              style={{
                                padding: "5px 0",
                                width: "40px",
                                height: "45px",
                                borderRadius: "50%",
                                marginLeft: "auto",
                              }}
                            />
                          </div>
                        )}
                        <div
                          className="d-flex justify-content-between align-items-center px-3 my-2"
                          style={{
                            border: "1px solid #BDBDBD",
                            borderRadius: "4px",
                            backgroundColor: medioPago === 'pvs-qr' ? '#f5f5f5' : 'white',
                          }}
                        >
                          <input
                            type="radio"
                            id="pvs-qr"
                            name="medioPago"
                            value="pvs-qr"
                            checked={medioPago === 'pvs-qr'}
                            onChange={(e) => setMedioPago(e.target.value)}
                          />
                          <label htmlFor="pvs-qr" className="mb-0 ml-3">
                            PVS QR
                          </label>
                          <Tooltip
                            title="Paga escaneando el código QR con tu app de banco"
                            placement="top"
                            arrow
                          >
                            <InfoOutlined fontSize="small" style={{ color: '#6c757d', cursor: 'help', marginLeft: "10px" }} />
                          </Tooltip>
                          <img
                            src={logoPVS}
                            alt="Logo PVS"
                            style={{
                              width: "45px",
                              height: "45px",
                              padding: "5px",
                              borderRadius: "50%",
                              marginLeft: "auto",
                            }}
                          />
                        </div>
                      </>
                    )}

                    {data?.medios_pago?.click_macro && param.id !== "457" && param.id !== "488" && (
                      <div
                        className="d-flex justify-content-between align-items-center px-3 my-2"
                        style={{
                          border: "1px solid #BDBDBD",
                          borderRadius: "4px",
                          backgroundColor: medioPago === 'clickmacro-qr' ? '#f5f5f5' : 'white',
                        }}
                      >
                        <input
                          type="radio"
                          id="clickmacro-qr"
                          name="medioPago"
                          value="clickmacro-qr"
                          checked={medioPago === 'clickmacro-qr'}
                          onChange={(e) => setMedioPago(e.target.value)}
                        />
                        <label htmlFor="clickmacro-qr" className="mb-0 ml-3">
                          Click Macro - QR
                        </label>
                        <img
                          src={logoClickMacro}
                          alt="Logo Click de Pago, Macro"
                          style={{
                            width: "45px",
                            height: "45px",
                            marginLeft: "auto",
                          }}
                        />
                      </div>
                    )}

                    {data?.medios_pago?.click_macro && param.id !== "459" && (
                      <div
                        className="d-flex justify-content-between align-items-center px-3 my-2"
                        style={{
                          border: "1px solid #BDBDBD",
                          borderRadius: "4px",
                          backgroundColor: medioPago === 'clickmacro-link' ? '#f5f5f5' : 'white',
                        }}
                      >
                        <input
                          type="radio"
                          id="clickmacro-link"
                          name="medioPago"
                          value="clickmacro-link"
                          checked={medioPago === 'clickmacro-link'}
                          onChange={(e) => setMedioPago(e.target.value)}
                        />
                        <label htmlFor="clickmacro-link" className="mb-0 ml-3">
                          Click Macro
                        </label>
                        <img
                          src={logoClickMacro}
                          alt="Logo Click de Pago, Macro"
                          style={{
                            width: "45px",
                            height: "45px",
                            marginLeft: "auto",
                          }}
                        />
                      </div>
                    )}

                    {!data?.medios_pago?.mercado_pago && !data?.medios_pago?.pagotic && !data?.medios_pago?.pvs && !data?.medios_pago?.click_macro && (
                      <p className="text-center text-muted">
                        No hay métodos de pago disponibles para este evento.
                      </p>
                    )}
                    <div
                      className="d-flex flex-row justify-content-end align-items-center gap-3"
                      style={{ paddingTop: "15px", paddingRight: "10px" }}
                    >
                      <p className="mb-0" style={{ fontSize: "14px" }}>
                        {format(
                          new Date(),
                          "E. d 'de' MMM. 'de' u HH:mm 'hs'",
                          {
                            locale: es,
                          }
                        )}
                      </p>
                      <div className="d-flex flex-row align-items-center">
                        <Tooltip
                          arrow
                          TransitionComponent={Zoom}
                          placement="bottom"
                          title="El costo de servicio de PassGo no es reembolsable"
                        >
                          <InfoOutlined
                            sx={{
                              width: "18px",
                              height: "18px",
                              marginRight: "5px",
                            }}
                          />
                        </Tooltip>
                        <p
                          className="mb-0"
                          style={{ fontSize: "18px", fontWeight: "500" }}
                        >
                          $ {totalFormateado}
                        </p>
                      </div>
                    </div>
                    <p
                      style={{
                        fontSize: "14px",
                        paddingTop: "8px",
                        paddingBotton: "15px",
                      }}
                      className="mb-3"
                    >
                      Al realizar el pago, acepta las{" "}
                      <a href="/">Condiciones de servicio de PassGo</a>
                    </p>
                  </div>
                </>
              )}

              {todasTarifasGratuitas() && (
                <>
                  <p
                    style={{
                      color: "#221A60",
                      fontSize: "24px",
                      marginBottom: "5px",
                    }}
                  >
                    Tickets gratuitos
                  </p>
                  <div style={{
                    width: "100%",
                    textAlign: "center",
                    padding: "20px",
                    marginBottom: "15px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px dashed #5656FF"
                  }}>
                    <Typography variant="h6" sx={{ color: "#5656FF", mb: 1 }}>
                      ¡Excelente!
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                      Todos los tickets seleccionados son gratuitos.
                      Haz clic en el botón para obtenerlos.
                    </Typography>
                  </div>
                </>
              )}
              {tieneLink && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxChecked}
                      onChange={handleCheckboxChangeTerminos}
                      required
                      size="small"
                    />
                  }
                  label={<small>Confirmo haber leído y acepto los términos y condiciones del organizador del evento</small>}
                />
              )}
              <Tooltip
                title={cooldownActive ? `Debes esperar antes de crear otro link de pago (${formatRemainingTime(cooldownRemaining)})` : ""}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    className="boton-submit w-100"
                    onClick={handleButtonClick}
                    disabled={
                      (tieneLink ? !checkboxChecked : false) ||
                      (multiplesMetodosPago && !medioPago && hayTarifasPagas()) || // la validacion de mp solo se hace si hay tarifas $$
                      loadingCarrito ||
                      buttonDisabled ||
                      cooldownActive
                    }
                  >
                    {loadingCarrito ? (
                      <>
                        <CircularProgress size="18px" sx={{ color: "white", marginRight: "10px" }} />
                        <span>Procesando...</span>
                      </>
                    ) : cooldownActive ? (
                      <>
                        <Timer sx={{ marginRight: "5px" }} />
                        <span>Espera {formatRemainingTime(cooldownRemaining)}</span>
                      </>
                    ) : (
                      <span>
                        <ShoppingCartOutlined
                          sx={{
                            color: "white",
                            width: "18px",
                            height: "18px",
                            marginRight: "5px",
                          }}
                        />{" "}
                        {todasTarifasGratuitas()
                          ? "Obtener tickets"
                          : (checkTarifasPreguntas() ? "Continuar con la compra" : "Finalizar compra")
                        }
                      </span>
                    )}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </Drawer >
      <SecondDrawer
        onBack={handleBack}
        handlePagoDirecto={handlePagoDirecto}
        dataTarifas={dataTarifas}
        setDataTarifas={setDataTarifas}
        loadingCarrito={loadingCarrito}
        cantidad={cantidad}
        open={openSecondDrawer}
        onClose={handleCloseDrawer}
        setOpenSecondDrawer={setOpenSecondDrawer}
        tarifasSeleccionadas={tarifasSeleccionadas} />
      {/* Modal para mostrar el QR de pago */}
      <Modal
        open={qrModalOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseQrModal();
          }
        }}
        disableEscapeKeyDown={true}
        aria-labelledby="modal-qr"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          /* maxHeight: '90vh',
          overflow: 'auto' */
        }}>
          {qrData && (
            <>
              <Typography variant="h6" component="h2" textAlign="center" sx={{ mb: 1, color: "#0b0062" }}>
                Escanea para pagar
              </Typography>
              <Typography variant="body2" textAlign="center" sx={{ mb: 1.5 }}>
                Utilizá tu billetera virtual o aplicación de pagos —como Mercado Pago, Modo, Ualá, etc.— para escanear el QR y completar el pago.
              </Typography>

              {qrData && (
                <Box sx={{ textAlign: 'center', mb: 2, display: 'flex', justifyContent: 'center' }}>
                  {qrData.provider === 'clickmacro' ? (
                    <QRCode value={qrData.qrData || qrData.qr_textual || ''} size={180} />
                  ) : qrData.provider === 'pvs' ? (
                    <img src={formatQRImage(qrData.qrImage || qrData.qrData)} alt="QR de pago PVS" />
                  ) : qrData.provider === 'mercadopago' ? (
                    <QRCode value={qrData.qrData} size={180} style={{
                      height: "180px",
                      width: "180px",
                      padding: "4px",
                      backgroundColor: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px"
                    }} />
                  ) : (
                    <img src={qrData.qrImage || qrData.qrData} alt="QR de pago" />
                  )}
                </Box>
              )}

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                backgroundColor: '#f9f9f9',
                p: 1.5,
                borderRadius: 1,
                mb: 2
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Importe</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ${(qrData.amount || qrData.monto_total || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Referencia</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {qrData.reference || qrData.referencia}
                  </Typography>
                </Box>
              </Box>

              {paymentStatus && (
                <Box sx={{
                  p: 1.5,
                  borderRadius: 1,
                  mb: 2,
                  backgroundColor: paymentStatus === 'APROBADO' ? '#d4edda' :
                    paymentStatus === 'PENDIENTE' ? '#fff3cd' : '#f8d7da',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Box component="span" sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: paymentStatus === 'APROBADO' ? '#28a745' :
                      paymentStatus === 'PENDIENTE' ? '#ffc107' : '#dc3545'
                  }} />
                  <Typography variant="body2" fontWeight="medium">
                    Estado: {paymentStatus}
                  </Typography>
                </Box>
              )}

              <Typography
                variant="caption"
                color="error"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <Timer fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                El QR expira en {formatRemainingTime(qrExpirationTime)}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCloseQrModal}
                  size="small"
                  sx={{ flex: 1, borderColor: "#5656ff" }}
                >
                  Cerrar
                </Button>

                <Button
                  className="boton-submit"
                  onClick={handleRefreshPayment}
                  size="small"
                  startIcon={<Refresh />}
                  sx={{ flex: 1 }}
                  disabled={!paymentReference}
                >
                  Actualizar
                </Button>
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}
              >
                ¿Necesitas ayuda? Contacta a nuestro equipo de soporte
              </Typography>
            </>
          )}

          {!qrData && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Cargando QR...</Typography>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};
export default FirstDrawerDesktop;
